import { useCallback, useEffect, useState, type ReactNode } from "react";
import styled from "@emotion/styled";
import { theme } from "../lib/theme";
import { media } from "../lib/breakpoints";

/**
 * A titled section that can be collapsed, with the state remembered per id.
 *
 * The Health page is one long scroll rather than tabs, which is only workable on a phone if the
 * reader can fold away the parts they aren't using. Persisting per id means a repeat visitor
 * lands in their own layout instead of re-collapsing the same five sections every time.
 *
 * `defaultOpen` is only the first-visit value — once a section has been toggled, the stored
 * preference wins, including when it disagrees with the default.
 */
const Wrapper = styled.section`
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.lg};
  margin-bottom: ${theme.spacing.md};

  ${media.sm} {
    border-radius: ${theme.radius.md};
    margin-bottom: ${theme.spacing.sm};
  }
`;

const Header = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  width: 100%;
  background: none;
  border: none;
  padding: ${theme.spacing.md};
  cursor: pointer;
  text-align: left;
  color: ${theme.colors.text};
  font-family: inherit;

  &:hover {
    background: ${theme.colors.bgHover};
  }

  ${media.sm} {
    padding: ${theme.spacing.sm};
  }
`;

const Chevron = styled.span<{ open: boolean }>`
  display: inline-flex;
  color: ${theme.colors.textMuted};
  transition: transform 0.15s ease;
  transform: rotate(${(p) => (p.open ? "90deg" : "0deg")});
  flex-shrink: 0;
`;

const Title = styled.h2`
  font-size: ${theme.fontSize.md};
  font-weight: 600;
  margin: 0;
`;

/** Right-aligned summary, so a collapsed section still carries its headline number. */
const Aside = styled.div`
  margin-left: auto;
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.textMuted};
  text-align: right;
  min-width: 0;
`;

const Body = styled.div`
  padding: 0 ${theme.spacing.md} ${theme.spacing.md};

  ${media.sm} {
    padding: 0 ${theme.spacing.sm} ${theme.spacing.sm};
  }
`;

const STORAGE_PREFIX = "clustec:section:";

function readStored(id: string): boolean | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + id);
    return raw == null ? null : raw === "1";
  } catch {
    // Private browsing or a blocked storage origin — fall back to the default.
    return null;
  }
}

export interface CollapsibleSectionProps {
  /** Stable key for the persisted open/closed state. */
  id: string;
  title: string;
  /** Shown in the header, so a collapsed section still reports its headline figure. */
  summary?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}

export function CollapsibleSection({
  id,
  title,
  summary,
  defaultOpen = true,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(() => readStored(id) ?? defaultOpen);

  // Re-read when the id changes so a remounted section under a new key picks up its own state.
  useEffect(() => {
    setOpen(readStored(id) ?? defaultOpen);
  }, [id, defaultOpen]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_PREFIX + id, next ? "1" : "0");
      } catch {
        // Not being able to remember the preference is not a reason to refuse the toggle.
      }
      return next;
    });
  }, [id]);

  return (
    <Wrapper>
      <Header onClick={toggle} aria-expanded={open}>
        <Chevron open={open} aria-hidden>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 6l6 6-6 6"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Chevron>
        <Title>{title}</Title>
        {summary && <Aside>{summary}</Aside>}
      </Header>
      {open && <Body>{children}</Body>}
    </Wrapper>
  );
}
